// ./src/components/features/VehicleTasks/VehicleTaskStatusSelector/VehicleTaskStatusSelector.tsx
//
// Chip-based single-select for vehicle task status (Todo / In Progress / Complete).
// Uses Mantine Chip.Group for a compact, touch-friendly UI.
// Supports clearable selection (clicking the active chip deselects it).
// Excludes REMOVED status as it's not user-selectable.
//
// Styling matches TravelTypeSelector pattern.

import React, { useMemo, useCallback } from "react";
import { Chip, Input, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { VEHICLE_TASK_STATUS_OPTIONS } from "@/config/vehicleTaskOptions";

import { VEHICLE_TASK_STATUS } from "@/utils/api/VehicleTaskService";
import type { VehicleTaskStatusValue } from "@/utils/api/VehicleTaskService";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface VehicleTaskStatusSelectorProps {
  /** Currently selected value (null = all / none selected) */
  value: VehicleTaskStatusValue | null;

  /** Callback when selection changes */
  onChange: (value: VehicleTaskStatusValue | null) => void;

  /** Optional label displayed above the chips */
  label?: string;

  /** Whether the field is required (disables deselect) */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Disable interaction */
  disabled?: boolean;

  /** Size of the input wrapper */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Chip variant */
  variant?: "outline" | "filled" | "light";
}

// ============================================
// Styles
// ============================================

const CHIP_LABEL_STYLES = {
  borderRadius: "var(--mantine-radius-md)",
  height: "42px",
  paddingLeft: "12px",
  paddingRight: "14px",
  fontSize: "var(--mantine-font-size-sm)",
  width: "100%",
  justifyContent: "center",
};

const CHIP_ICON_WRAPPER_STYLES = {
  width: "auto",
  height: "20px",
  paddingRight: "4px",
};

const CHIP_ROOT_STYLES = {
  flex: "1 1 0",
  minWidth: 0,
};

const GROUP_STYLES: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--mantine-spacing-xs)",
};

// ============================================
// Component
// ============================================

const VehicleTaskStatusSelectorComponent: React.FC<VehicleTaskStatusSelectorProps> = ({
  value,
  onChange,
  label,
  required = false,
  error,
  disabled = false,
  size = "md",
  variant = "outline",
}) => {
  const { t } = useTranslation(["vehicleTasks"]);

  const defaultLabel = label ?? t("vehicleTasks:filters.status", { defaultValue: "Status" });

  // Filter out REMOVED status — not user-selectable
  const filteredOptions = useMemo(
    () => VEHICLE_TASK_STATUS_OPTIONS.filter((opt) => opt.value !== VEHICLE_TASK_STATUS.REMOVED),
    []
  );

  const handleChange = useCallback(
    (selected: string) => {
      if (value !== null && selected === String(value) && !required) {
        onChange(null);
      } else {
        onChange(selected ? (Number(selected) as VehicleTaskStatusValue) : null);
      }
    },
    [value, required, onChange]
  );

  const chips = useMemo(() => {
    return filteredOptions.map((option) => (
      <Chip
        key={option.value}
        value={String(option.value)}
        color={option.color}
        variant={variant}
        size="sm"
        disabled={disabled}
        icon={<AppIcon iconName={option.icon || ''} size={20} />}
        styles={{
          label: CHIP_LABEL_STYLES,
          iconWrapper: CHIP_ICON_WRAPPER_STYLES,
          root: CHIP_ROOT_STYLES,
        }}
      >
        {t(`vehicleTasks:${option.labelKey}`)}
      </Chip>
    ));
  }, [filteredOptions, t, variant, disabled]);

  return (
    <Input.Wrapper
      label={defaultLabel}
      required={required}
      error={error}
      size={size}
    >
      <Box mt={4} style={GROUP_STYLES}>
        <Chip.Group
          multiple={false}
          value={value !== null ? String(value) : ""}
          onChange={handleChange}
        >
          {chips}
        </Chip.Group>
      </Box>
    </Input.Wrapper>
  );
};

export const VehicleTaskStatusSelector = React.memo(VehicleTaskStatusSelectorComponent);